import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import { ENDPOINTS } from "@/api/endpoints";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, Wrench, X, Save } from "lucide-react";

export default function EquipmentManagement() {
  const [showForm, setShowForm] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const queryClient = useQueryClient();

  // Fetch equipment using React Query
  const { data: equipment = [], isLoading } = useQuery({
    queryKey: ["equipment"],
    queryFn: () => apiClient.get(ENDPOINTS.SETTINGS.EQUIPMENT),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const [formData, setFormData] = useState({
    name: "",
    category: "",
    model: "",
    serialNumber: "",
    status: "active",
  });

  // Mutations for equipment operations
  const createEquipmentMutation = useMutation({
    mutationFn: (equipmentData) => apiClient.post(ENDPOINTS.SETTINGS.EQUIPMENT, equipmentData),
    onSuccess: () => {
      queryClient.invalidateQueries(["equipment"]);
      resetForm();
    },
  });

  const updateEquipmentMutation = useMutation({
    mutationFn: ({ id, data }) => apiClient.put(`${ENDPOINTS.SETTINGS.EQUIPMENT}/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["equipment"]);
      resetForm();
    },
  });

  const deleteEquipmentMutation = useMutation({
    mutationFn: (equipmentId) => apiClient.delete(`${ENDPOINTS.SETTINGS.EQUIPMENT}/${equipmentId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(["equipment"]);
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      category: "",
      model: "",
      serialNumber: "",
      status: "active",
    });
    setEditingEquipment(null);
    setShowForm(false);
  };

  const handleAdd = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = (eq) => {
    setFormData({
      name: eq.name || "",
      category: eq.category || "",
      model: eq.model || "",
      serialNumber: eq.serialNumber || "",
      status: eq.status || "active",
    });
    setEditingEquipment(eq);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (editingEquipment) {
      updateEquipmentMutation.mutate({ id: editingEquipment.id, data: formData });
    } else {
      createEquipmentMutation.mutate(formData);
    }
  };

  const handleDelete = async (equipmentId) => {
    if (window.confirm("Are you sure you want to delete this equipment?")) {
      deleteEquipmentMutation.mutate(equipmentId);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const statusColors = {
    active: "bg-green-100 text-green-700",
    maintenance: "bg-orange-100 text-orange-700",
    inactive: "bg-gray-100 text-gray-700",
  };

  const categoryColors = {
    medical_device: "bg-blue-100 text-blue-700",
    iv_equipment: "bg-indigo-100 text-indigo-700",
    diagnostic: "bg-cyan-100 text-cyan-700",
    monitoring: "bg-purple-100 text-purple-700",
    furniture: "bg-yellow-100 text-yellow-700",
    computer: "bg-green-100 text-green-700",
    laboratory: "bg-pink-100 text-pink-700",
    other: "bg-gray-100 text-gray-700",
  };

  return (
    <div className="space-y-6">
      <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5" />
              Equipment Management
            </CardTitle>
            <Button onClick={handleAdd} className="gap-2" data-testid="equipment-add-btn">
              <Plus className="w-4 h-4" />
              Add Equipment
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {showForm && (
            <div className="mb-6 p-4 border rounded-lg bg-slate-50">
              <form onSubmit={handleSave} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Equipment Name *</Label>
                    <Input
                      id="name"
                      data-testid="equipment-name-input"
                      value={formData.name}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      placeholder="e.g., IV Infusion Pump"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category">Category *</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) =>
                        handleInputChange("category", value)
                      }
                    >
                      <SelectTrigger data-testid="equipment-category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="medical_device" data-testid="category-medical_device">Medical Device</SelectItem>
                        <SelectItem value="iv_equipment" data-testid="category-iv_equipment">IV Equipment</SelectItem>
                        <SelectItem value="diagnostic" data-testid="category-diagnostic">Diagnostic</SelectItem>
                        <SelectItem value="monitoring" data-testid="category-monitoring">Monitoring</SelectItem>
                        <SelectItem value="furniture" data-testid="category-furniture">Furniture</SelectItem>
                        <SelectItem value="computer" data-testid="category-computer">Computer</SelectItem>
                        <SelectItem value="laboratory" data-testid="category-laboratory">Laboratory</SelectItem>
                        <SelectItem value="other" data-testid="category-other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="model">Model</Label>
                    <Input
                      id="model"
                      data-testid="equipment-model-input"
                      value={formData.model}
                      onChange={(e) =>
                        handleInputChange("model", e.target.value)
                      }
                      placeholder="e.g., Infusion Pro 2000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="serialNumber">Serial Number</Label>
                    <Input
                      id="serialNumber"
                      data-testid="equipment-serial-input"
                      value={formData.serialNumber}
                      onChange={(e) =>
                        handleInputChange("serialNumber", e.target.value)
                      }
                      placeholder="e.g., SN123456789"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) =>
                        handleInputChange("status", value)
                      }
                    >
                      <SelectTrigger data-testid="equipment-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active" data-testid="equipment-status-active">Active</SelectItem>
                        <SelectItem value="maintenance" data-testid="equipment-status-maintenance">Maintenance</SelectItem>
                        <SelectItem value="inactive" data-testid="equipment-status-inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={resetForm}>
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="equipment-save-btn">
                    <Save className="w-4 h-4 mr-2" />
                    {editingEquipment ? "Update" : "Add"} Equipment
                  </Button>
                </div>
              </form>
            </div>
          )}

          <div className="overflow-x-auto -mx-6 px-6">
            <div className="min-w-full inline-block align-middle">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[150px]">Name</TableHead>
                    <TableHead className="min-w-[100px]">Category</TableHead>
                    <TableHead className="min-w-[100px]">Model</TableHead>
                    <TableHead className="min-w-[120px]">Serial Number</TableHead>
                    <TableHead className="min-w-[100px]">Status</TableHead>
                    <TableHead className="min-w-[150px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan="6" className="text-center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : (
                    equipment.map((eq, index) => (
                      <TableRow key={eq.id} data-testid={`equipment-row-${index}`}>
                        <TableCell className="font-medium">
                          {eq.name}
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={`${categoryColors[eq.category] || "bg-gray-100 text-gray-700"} border-0 whitespace-nowrap`}
                          >
                            {eq.category?.replace(/_/g, " ")}
                          </Badge>
                        </TableCell>
                        <TableCell>{eq.model || "-"}</TableCell>
                        <TableCell>{eq.serialNumber || "-"}</TableCell>
                        <TableCell>
                          <Badge
                            className={`${statusColors[eq.status]} border-0 whitespace-nowrap`}
                          >
                            {eq.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(eq)}
                              title="Edit Equipment"
                              data-testid={`edit-equipment-${index}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(eq.id)}
                              className="text-red-600 hover:text-red-700"
                              title="Delete Equipment"
                              data-testid={`delete-equipment-${index}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}